import { memo } from 'react'
import { List, ListItem, ListItemButton, ListItemText, SxProps } from '@mui/material'
import { APIProvider } from '@vis.gl/react-google-maps'
import { ENV } from '@/utils'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import Input from '../Input'
import { PlaceAutocompleteProps, PredictionsDropdownProps } from './PlaceAutocomplete.types'
import { usePlaceAutocompleteController } from './PlaceAutocomplete.controller'
import { placeAutocompleteStyles as styles } from './PlaceAutocomplete.styles'
import { DEFAULT_DEBOUNCE_MS } from './PlaceAutocomplete.data'
import { useTranslations } from '@/core/translation'

const PlaceAutocompleteInner = ({
  value = '',
  onChange,
  onAddressSelect,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  countries,
  ref: _ref,
  ...props
}: PlaceAutocompleteProps) => {
  const t = useTranslations('components')

  const { predictions, loading, onInputChange, onSelect } = usePlaceAutocompleteController({
    value,
    debounceMs,
    countries,
    onChange,
    onAddressSelect,
  })

  return (
    <Input
      value={value}
      onChange={onInputChange}
      DropDown={
        predictions.length > 0 && (
          <PredictionsDropdown
            predictions={predictions}
            loading={loading}
            loadingText={t('placeAutocomplete.loading')}
            onSelect={onSelect}
          />
        )
      }
      dropdownProps={{ sx: styles.dropdown, wrapperSx: styles.dropdownWrapperSx }}
      {...props}
    />
  )
}

function PredictionsDropdown({
  predictions,
  loading,
  loadingText,
  onSelect,
}: PredictionsDropdownProps) {
  return (
    <List sx={styles.list}>
      {loading && (
        <ListItem sx={{ ...styles.listItem, ...styles.listItemButton } as SxProps}>
          <ListItemText primary={loadingText} />
        </ListItem>
      )}
      {!loading &&
        predictions.map((prediction) => (
          <ListItem key={prediction.placeId} sx={styles.listItem}>
            <ListItemButton sx={styles.listItemButton} onClick={() => onSelect(prediction)}>
              <Box>
                <Text sx={styles.name}>{prediction.title}</Text>
                <Text sx={styles.location}>{prediction.location}</Text>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
    </List>
  )
}

const PlaceAutocomplete = (props: PlaceAutocompleteProps) => {
  return (
    <APIProvider apiKey={ENV.GOOGLE_MAP_KEY}>
      <PlaceAutocompleteInner {...props} />
    </APIProvider>
  )
}

export default memo(PlaceAutocomplete)
